/**
 * Bridges a saved payment method onto the account formatter the payment tables already share, so
 * the Payment Center's default-method strip masks numbers and formats expiries exactly the way
 * Upcoming Payments and the transaction history do.
 */
import type { CardNetwork } from '@/graphql/generated/graphql';
import type { SavedPaymentAccount } from '@/hooks/payments/paymentApi';
import type { getAccountDisplay } from './accountDisplay.format';

/**
 * Maps a `SavedPaymentAccount` onto {@link getAccountDisplay}'s flat input. The formatter was built
 * for `PaymentTransaction`/`UpcomingPayment`, which carry a `PaymentAccountKind` enum and split the
 * descriptor across `network`/`bankName`; saved methods collapse both into one `descriptor`, so this
 * re-splits them. The input type is not exported, hence the `Parameters<>` lookup — the same
 * approach `AccountCell` takes.
 */
export function toAccountDisplayInput(account: SavedPaymentAccount): Parameters<typeof getAccountDisplay>[0] {
  const isCard = account.kind === 'card';
  return {
    accountKind: isCard ? 'CARD' : 'BANK',
    // `descriptor` holds the card network code for cards and the bank's name for bank accounts; the
    // cast is safe because it originates from the schema's own `CardNetwork` field.
    network: isCard ? (account.descriptor as CardNetwork) : null,
    bankName: isCard ? null : account.descriptor,
    accountSuffix: account.suffix,
    expirationMonth: account.expirationMonth,
    expirationYear: account.expirationYear,
  };
}
